import { BaseService } from './base.service';
import { 
  LoginCredentials, 
  AuthResponse,
  RefreshTokenResponse
} from '../types/auth';
import { UserLocation } from '../types/location.types';
import api from './api';

export class AuthService extends BaseService {
  private static instance = new AuthService();

  private constructor() {
    super('/auth');
  }

  async fetchUserLocation(): Promise<UserLocation | null> {
    try {
      const response = await api.get('/member-locations/my-location');
      const location = response.data;
      
      // Store location in localStorage
      localStorage.setItem('userLocation', JSON.stringify(location));
      
      return location;
    } catch (error) {
      console.error('Failed to fetch user location:', error);
      return null;
    }
  }

  private async loginInternal(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      return await this.post<AuthResponse>('/login', {
        token: credentials.token,
        recaptchaToken: credentials.recaptchaToken,
        send_to: credentials.send_to,
        mode: credentials.mode,
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Invalid credentials';
      throw new Error(message);
    }
  }

  private async logoutInternal(): Promise<void> {
    try {
      await this.post('/logout');
    } catch (error) {
      console.error('Logout failed:', error);
      // Don't throw error on logout failure
    } finally {
      // Clear user location from localStorage on logout
      localStorage.removeItem('userLocation');
    }
  }

  private async refreshTokenInternal(): Promise<RefreshTokenResponse> {
    try {
      return await this.post<RefreshTokenResponse>('/refresh-token');
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  }

  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.instance.loginInternal(credentials);
  }

  static async fetchUserLocation(): Promise<UserLocation | null> {
    return this.instance.fetchUserLocation();
  }

  static async logout(): Promise<void> {
    return this.instance.logoutInternal();
  }

  static async refreshToken(): Promise<RefreshTokenResponse> {
    return this.instance.refreshTokenInternal();
  }

  static async requestAccountDeletion(): Promise<void> {
    await api.post('/member-profile/request-deletion');
  }
}
